<template>
    <div class="ur-counter ur-input" :class="inputClass">
        <ur-icon-button class="ur-counter__button ur-button--minus"
                v-if="getType==='addsubstract'" key="first"
                :disabled="_minusCtrlDisabled"
                @click="decreaseNum">remove</ur-icon-button>
        <input class="ur-counter__input"
               type="number" ref="input"
               :min="$_min"
               :max="$_max"
               :step="step"
               v-model="currentValue"
               :placeholder="placeholder"
               :disabled="disabled || (!decreasable && !increasable)"
               :readonly="readonly || !editable"
               autocomplete="off"
               @wheel.stop="()=>{}"
               @input="input"
               @blur="blur"
               @change="change"
               @keyup.enter="enterHandler"
               @keypress="isNumber($event)"/>
        <ur-icon-button class="ur-counter__button ur-button--plus" v-if="getType==='addsubstract'"
            key="second" :disabled="_plusCtrlDisabled"
            @click="increaseNum">add</ur-icon-button>
        <div class="ur-counter__updown-wrapper" v-if="getType==='updown'">
            <div class="ur-counter__updown-upper" key="second" borderless :disabled="_plusCtrlDisabled" @click="increaseNum"/>
            <div class="ur-counter__updown-divider"/>
            <div class="ur-counter__updown-lower" key="first" borderless :disabled="_minusCtrlDisabled" @click="decreaseNum"/>
        </div>
    </div>
</template>

<script>
    const isNaN = Number.isNaN || window.isNaN;
    // const REGEXP_NUMBER = /^-?(?:\d+|\d+\.\d+|\.\d+)(?:[eE][-+]?\d+)?$/;
    const REGEXP_DECIMALS = /\.\d*(?:0|9){10}\d*$/;
    const normalizeDecimalNumber = (value, times = 100000000000) => (
        REGEXP_DECIMALS.test(value) ? (Math.round(value * times) / times) : value
    );
    const ALIGN_OPTS = ['left', 'center','right'];
    const SIZE_OPTS = ['small','medium','large'];
    const TYPE_OPTS = ['updown', 'addsubstract', 'nocontrol'];

    // ur-stepper는 설정에 따라 'updown', 'addsubstract', 'nocontrol' 세 가지 형태로 제공하며,
    // 입력값을 받거나 클릭 이벤트에 의해 값이 증거하거나 감소합니다.
    // #ur-stepper provides three types of 'updown', 'addsubstract', and 'nocontrol' depending on the setting.
    // #The value is evidenced or decremented by an input or click event.
    export default {
        name: 'ur-stepper',
        model: {
            event: 'change',
        },
        inject: {
            urInput: {from:'urInput', default: () => undefined}
        },
        props: {
            // $ver: v0.2.0
            // 컨트롤의 크기입니다. 'large','medium','small'의 값을 입력할 수 있습니다.
            // #The size of the control. You can enter values ​​for 'large', 'medium', 'small'.
            // $default: 'medium'
            // $tags: IDE
            size     : {type: String},

            // $ver: v0.2.0
            // 컨트롤의 종류입니다. 'updown', 'addsubstract', 'nocontrol'.
            // #The type of the control. You can enter values ​​for 'updown', 'addsubstract', 'nocontrol'.
            // $default: 'addsubstract'
            // $tags: IDE
            controlType     : {type: String, default: undefined},

            // $ver: v0.2.0
            // 입력 값의 정렬입니다. 'left', 'center','right'
            // #The alignment of the values ​​in the input fields. You can enter values ​​for 'left', 'center','right'.
            // $default: 'center'
            // $tags: IDE
            alignment     : {type: String},

            // $ver: v0.1.0
            // 가질 수 있는 최소값 입니다.
            // #The minimum value you can have.
            // $tags: IDE
            min      : {type: [String,Number],default: -Infinity}, 

            // $ver: v0.1.0
            // 가질 수 있는 최대값 입니다.
            // #The maximum value you can have.
            // $tags: IDE
            max      : {type: [String,Number],default: Infinity}, 

            // $ver: v0.1.0
            // 증가 혹은 감소 버튼을 클릭했을 때 변경되는 값의 크기입니다.
            // #The size of the value that changes when the increase or decrease button is clicked.
            // $tags: IDE
            step     : {type: [Number,String],default: 1,},

            // $ver: v0.1.0
            // $v-model
            // v-model로 바인딩 되는 값으로, 현재 컴포넌트의 값 입니다.
            // #The value to be bound to the v-model, the value of the current component.
            // $tags: IDE
            value    : {type: [Number, String],default: NaN,},

            // $ver: v0.1.0
            // 비활성화 여부입니다.
            // #Whether to disable
            // $default: false
            // $tags: IDE
            disabled : {type: Boolean,default: false},

            // $ver: v0.1.0
            // 읽기전용 여부입니다. disabled 속성이 활성화돼있을 경우 무시됩니다.
            // #Whether to read only. If the disabled attribute is activated, the readonly attribute is ignored.
            // $default: false
            // $tags: IDE
            readonly : {type: Boolean,default: false},

            // $ver: v0.1.0
            // 값이 비어있을 경우 입력칸에 표시할 텍스트 입니다.
            // #The text to display in the input field if the value is empty.
            placeholder: {type: String,default: undefined},

            // $ver: v0.1.0
            // 입력 칸 Key-in 수정가능 여부입니다. disabled 혹은 readonly 속성이 활성화돼있을 경우 무시됩니다.
            // #Input field Key-in can be modified. If the disabled or readonly attribute is activated, the editable attribute is ignored.
            // $default: true
            editable : {type: Boolean,default: true},

            // $ver: v0.1.0
            // 증가 혹은 감소 버튼의 숨김처리 여부입니다.
            // #Whether the increase or decrease button is hidden
            // $default: false
            noControl: {type: Boolean,default: false},

            // $ver: v0.1.0 
            // 증가/감소 버튼을 상하로 표현합니다.
            // #Increase / decrease button up and down.
            // $default: false
            updown: {type: Boolean,default: false},

            // $ver: v0.1.0
            // 입력 칸의 값을 좌측 정렬 합니다.
            // #Left-justify the value in the input field.
            // $default: false
            left     : {type: Boolean,default: false},

            // $ver: v0.1.0
            // 입력 칸의 값을 중앙 정렬 합니다.
            // #Center the values ​​in the input fields.
            // $default: false
            center   : {type: Boolean,default: false},

            // $ver: v0.1.0
            // 입력 칸의 값을 우측 정렬 합니다.
            // #Right-justify the value in the input field.
            // $default: false
            right    : {type: Boolean,default: false},

            // $ver: v0.1.2
            // 컨트롤의 크기를 Large 사이즈로 조정합니다.
            // #Resize the control to Large size.
            // $default: false
            large: {type:Boolean, default: false},
            
            // $ver: v0.1.2
            // 컨트롤의 크기를 Medium 사이즈로 조정합니다.
            // #Resize the control to Medium size.
            // $default: false
            medium: {type:Boolean, default: false},
            
            // $ver: v0.1.2
            // 컨트롤의 크기를 Small 사이즈로 조정합니다.
            // #Resize the control to Small size.
            // $default: false
            small: {type:Boolean, default: false},

            // $ver: v0.2.1
            // 컨트롤에 따라 값이 최소치와 최대치 사이를 반복합니다. 
            // #Repeact the range between minimum and maximum value.
            // $default: false
            repeat: {type:Boolean, default: false},
        },
        data: function () {
            return {
                default: {
                    size: 'medium',
                    align: 'center',
                    type: 'addsubstract'
                },
                currentValue: NaN,
            }
        },
        computed: {
            $_min() {return this.min===-Infinity?this.min:parseInt(this.min);},
            $_max() {return this.max===Infinity?this.max:parseInt(this.max);},
            _plusCtrlDisabled() {
                if (this.disabled || this.readonly) return true;
                if (this.repeat) return false;
                return !this.increasable
            },
            _minusCtrlDisabled() {
                if (this.disabled || this.readonly) return true;
                if (this.repeat) return false;
                return !this.decreasable
            },
            getType() {
                if (this.controlType && TYPE_OPTS.includes(this.controlType)) {
                    return this.controlType
                }
                if (this.noControl) return 'nocontrol'
                if (this.updown) return 'updown'

                return this.default.type
            },
            lowerButtonStyle(){
                return "ur-counter__button ur-button--lower ";
            },
            upperButtonStyle(){
                return "ur-counter__button ur-button--upper"
            },
            increasable() {
                const num = this.currentValue;
                return isNaN(num) || num < this.$_max;
            },
            decreasable() {
                const num = this.currentValue;
                return isNaN(num) || num > this.$_min;
            },
            cSize() {
                if (this.size && SIZE_OPTS.includes(this.size)) {
                    return this.size;
                }
                if (this.large) return 'large';
                if (this.medium) return 'medium';
                if (this.small) return 'small';
                if (this.urInput) return this.urInput.size;
                return this.default.size;
            },
            cAlign() {
                if (this.alignment && ALIGN_OPTS.includes(this.alignment)) {
                    return this.alignment;
                }
                if (this.left) return 'left';
                if (this.right) return 'right';
                if (this.center) return 'center';
                
                return this.default.align;
            },
            inputClass() {
                let clazz = [];
                clazz.push('ur-counter--'+this.cSize);
                clazz.push('ur-counter--'+this.cAlign);

                if (this.disabled) {
                    clazz.push("ur-counter--disabled");
                } else if (this.readonly) {
                    clazz.push("ur-counter--readonly");
                }
                if (!this.editable) {
                    clazz.push('ur-counter--not-editable');
                }
                return clazz;
            },
            buttonSize() {
                let buttonSize = {};
                buttonSize[this.cSize] = true;
                return buttonSize;
            },
        },
        watch: {
            value: {
                immediate: true,
                handler(newValue, oldValue) {
                    if (
                        // Avoid triggering change event when created
                        !(isNaN(newValue) && typeof oldValue === 'undefined')
                        // Avoid infinite loop
                        && newValue !== this.currentValue
                    ) {
                        this.setValue(newValue);
                    }
                },
            },
        },
        methods: {
            input(e) {
                // $ver: v0.1.0
                // $return: value
                // 입력 칸에 직접 입력할 때, 현재 값을 반환하며 이벤트를 발생시킵니다.
                // #When typing directly into the input field, returns the current value and fires an event.
                this.$emit('input',this.currentValue);
            },
            blur(e) {
                // $ver: v0.1.2
                // $return: $event
                // 입력칸에서 focus out될 때 이벤트를 발생시킵니다
                // #Raises an event when it's focused out of the input field
                this.$emit('blur',e);
            },
            change(event) {
                this.setValue(Math.min(this.$_max, Math.max(this.$_min, event.target.value)));
            },

            // @vuese
            // $ver: v0.0.1
            // $return: N/A
            // 값을 한 단계 증가시킵니다.
            // #Increment the value by one step.
            increaseNum() {
                if (!this.readonly && !this.disabled) {
                    if (this.increasable) {
                        let { currentValue } = this;
                        if (isNaN(currentValue)) {
                            currentValue = 0;
                        }

                        let step = parseFloat(this.step);
                        if (!step) step = 0;

                        this.setValue(Math.min(this.$_max, Math.max(
                            this.$_min,
                            normalizeDecimalNumber(currentValue + step),
                        )));
                    } else if (this.repeat) {
                        this.setValue(this.$_min)
                    }
                }
            },

            // @vuese
            // $ver: v0.0.1
            // $return: N/A
            // 값을 한 단계 감소시킵니다.
            // #Decreases the value by one step.
            decreaseNum() {
                if (!this.readonly && !this.disabled) {
                    if (this.decreasable) {
                        let { currentValue } = this;
                        if (isNaN(currentValue)) {
                            currentValue = 0;
                        }

                        let step = parseFloat(this.step);
                        if (!step) step = 0;

                        this.setValue(Math.min(this.$_max, Math.max(
                            this.$_min,
                            normalizeDecimalNumber(currentValue - step),
                        )));
                    } else if (this.repeat) {
                        this.setValue(this.$_max)
                    }
                }
            },

            // @vuese
            // $ver: v0.0.1
            // $arg: Number
            // $return: N/A
            // 입력된 값으로 변경합니다.
            // #Change to the entered value.
            setValue(value) {
                const oldValue = this.currentValue;
                if (this.$_min <= this.$_max) {
                    if (value < this.$_min) {
                        value = this.$_min
                    }
                    // value = Math.min(this.$_max, Math.max(this.$_min, value));
                }
                this.currentValue = parseInt(value);
                if (value === oldValue) {
                    // Force to override the number in the input box (#13).
                    this.$refs.input.value = value;
                }
                // $ver: v0.0.1
                // $return: 현재 값, 기존 값
                // 값이 변경될 때 이벤트를 발생시킵니다.
                // #Raises an event when the value changes.
                this.$emit('change', value, oldValue);
            },
            enterHandler() {
                this.setValue(this.currentValue);
                // $ver: v0.0.1
                // $return: 현재 값
                // 입력 칸에서 엔터키 누를 시 이벤트를 발생시킵니다.
                // #Fires an event when the Enter key is pressed in the input box.
                this.$emit('enter', this.currentValue);
            },
            isNumber(evt) {
                evt = (evt) ? evt : window.event;
                var charCode = (evt.which) ? evt.which : evt.keyCode;
                if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
                    evt.preventDefault();;
                } else {
                    return true;
                }
            },
        }
    };

</script>
